/**
 * Regras de Negócio Centralizadas para Tarefas
 * ÚNICA fonte de verdade para cálculos e validações
 */

import { calculateEndDate, calculateDuration, getNextWorkDay, addDays, isDateHoliday } from './dateUtils';
import { applyLag, DEFAULT_LAG, parsePredecessorWithLag, isValidLag } from './lagUtils';
import type { Task, ProjectSettings, DependencyLag } from '../types';

interface DateSettings {
  workDays: number[];
  holidays: string[];
  hoursPerDay: number;
}

export interface TaskDateUpdates {
  startDate?: Date;
  endDate?: Date;
  duration?: string;
  dependencies?: string[];
  dependencyLags?: Record<string, DependencyLag | string>;
  [key: string]: unknown;
}

interface DependentTaskDates {
  startDate: Date;
  endDate: Date;
}

export interface CascadeUpdate {
  taskId: string;
  startDate?: Date;
  endDate?: Date;
  duration?: string;
  progress?: number;
}

export interface ProcessTaskUpdateResult {
  taskUpdates: TaskDateUpdates;
  cascadeUpdates: CascadeUpdate[];
}

interface ParsedPredecessorResult {
  dependencies: string[];
  dependencyLags: Record<string, string>;
}

interface CleanedPredecessorsResult {
  cleanedDependencies: string[];
  removedCount: number;
}

/**
 * Obter configurações de data do projeto (evita duplicação)
 */
/**
 * Lê os feriados/exceções do calendário do projeto.
 *
 * A chave canônica é `excludedDates`: é a que o modal de configurações grava e a
 * que o cronograma sempre respeitou. O fallback para `holidays` existe só para
 * projetos criados pela IA antes da unificação, que gravavam a chave errada e
 * por isso tinham feriados invisíveis para o cronograma. Ler as duas aqui evita
 * migration de dados em produção, que é manual neste projeto.
 *
 * ÚNICO leitor autorizado dessas chaves. Quem ler `settings.excludedDates` na
 * mão em outro arquivo reabre o descasamento que esta função existe para fechar.
 */
export const getProjectHolidays = (settings: ProjectSettings | null | undefined): string[] => {
  const bruto = settings as Record<string, unknown> | null | undefined;
  const canonico = bruto?.excludedDates as string[] | undefined;
  if (canonico && canonico.length > 0) return canonico;
  return (bruto?.holidays as string[] | undefined) || [];
};

export const getProjectDateSettings = (project: { settings?: ProjectSettings } | null | undefined): DateSettings => ({
  workDays: project?.settings?.workDays || [1, 2, 3, 4, 5],
  holidays: getProjectHolidays(project?.settings),
  hoursPerDay: project?.settings?.hoursPerDay || 8
});

/**
 * Verifica se uma data é dia útil (não é fim de semana nem feriado)
 */
export const isWorkDay = (date: Date | string, workDays: number[], holidays: string[]): boolean => {
  const d = new Date(date);
  return workDays.includes(d.getDay()) && !isDateHoliday(d, holidays);
};

/**
 * Ajusta data para próximo dia útil se necessário
 * Se já for dia útil, retorna a mesma data
 */
export const ensureWorkDay = (date: Date | string, workDays: number[], holidays: string[]): Date => {
  if (isWorkDay(date, workDays, holidays)) return new Date(date);
  return getNextWorkDay(date, workDays, holidays);
};

/**
 * Valida e normaliza datas de uma tarefa
 * Aplica todas as regras de negócio para datas
 *
 * @param {Object} task - Tarefa atual (para obter valores existentes)
 * @param {Object} updates - Campos sendo atualizados
 * @param {Object} settings - Configurações do projeto (workDays, holidays, hoursPerDay)
 * @returns {Object} Updates normalizados com datas calculadas
 */
export const validateAndNormalizeTaskDates = (task: Task, updates: TaskDateUpdates, settings: DateSettings): TaskDateUpdates => {
  const { workDays, holidays, hoursPerDay } = settings;
  const result: TaskDateUpdates = { ...updates };

  // CASO ESPECIAL: Se ambos startDate e endDate são enviados, manter ambos e recalcular duration
  // (usado para resize-left no Gantt - move início mantendo o fim)
  if (updates.startDate && updates.endDate) {
    result.startDate = ensureWorkDay(updates.startDate, workDays, holidays);
    result.endDate = updates.endDate; // Mantém o endDate original
    result.duration = calculateDuration(result.startDate, result.endDate, workDays, holidays);
    return result;
  }

  // Se está definindo startDate (sem endDate), ajustar para dia útil e recalcular endDate
  if (updates.startDate) {
    result.startDate = ensureWorkDay(updates.startDate, workDays, holidays);
    // Recalcular endDate se tiver duração
    const duration = (updates.duration || task?.duration) as string | null | undefined;
    if (duration) {
      result.endDate = calculateEndDate(
        result.startDate,
        duration,
        workDays,
        holidays,
        hoursPerDay
      );
    }
  }

  // Se está definindo duration (sem startDate no update), recalcular endDate
  if (updates.duration && !updates.startDate) {
    const startDate = task?.startDate;
    if (startDate) {
      result.endDate = calculateEndDate(startDate, updates.duration as string, workDays, holidays, hoursPerDay);
    }
  }

  // Se está definindo endDate diretamente (sem duration e sem startDate no update), recalcular duration
  // IMPORTANTE: Se startDate também foi passado, é uma operação de "mover" e a duração deve ser preservada
  // (usado para resize-right no Gantt)
  if (updates.endDate && !updates.duration && !updates.startDate) {
    const startDate = task?.startDate;
    if (startDate) {
      // Ajustar endDate para dia útil (para trás se cair em fim de semana/feriado)
      let adjustedEndDate = new Date(updates.endDate);
      while (!workDays.includes(adjustedEndDate.getDay()) || isDateHoliday(adjustedEndDate, holidays)) {
        adjustedEndDate.setDate(adjustedEndDate.getDate() - 1); // Move para trás
      }
      result.endDate = adjustedEndDate;
      result.duration = calculateDuration(startDate, adjustedEndDate, workDays, holidays);
    }
  }

  return result;
};

/**
 * Calcula datas de uma tarefa baseado em suas dependências (predecessoras)
 * Implementa lógica Finish-to-Start (FS) com suporte a lag/lead
 *
 * @param {Object} task - Tarefa que tem dependências
 * @param {Array} predecessors - Array de tarefas predecessoras
 * @param {Object} settings - Configurações do projeto
 * @param {Object} dependencyLags - Objeto com lag por predecessor { predecessorId: '+2d' }
 * @returns {Object|null} Novas datas {startDate, endDate} ou null se não for possível calcular
 */
export const calculateDependentTaskDates = (task: Task, predecessors: Task[], settings: DateSettings, dependencyLags: Record<string, DependencyLag | string> = {}): DependentTaskDates | null => {
  const { workDays, holidays, hoursPerDay } = settings;

  if (!predecessors || predecessors.length === 0) return null;

  // Para cada predecessor, calcular a data de início considerando seu lag específico
  const resultDates: Date[] = predecessors
    .filter(p => p.endDate)
    .map(p => {
      const lag = dependencyLags[p.id] || DEFAULT_LAG;
      return applyLag(new Date(p.endDate!), lag as string, { workDays, holidays });
    });

  if (resultDates.length === 0) return null;

  // Usar a MAIOR data resultante (mais tarde) entre todos os predecessores
  const newStartDate = new Date(Math.max(...resultDates.map(d => d.getTime())));
  const newEndDate = calculateEndDate(newStartDate, task.duration || '1d', workDays, holidays, hoursPerDay);

  return { startDate: newStartDate, endDate: newEndDate };
};

/**
 * Propaga alterações para tarefas sucessoras (cascata)
 * Recalcula datas de todas as tarefas que dependem da tarefa alterada
 * Respeita os lags configurados em cada sucessor
 *
 * @param {Array} flatTasks - Lista flat de todas as tarefas
 * @param {string} changedTaskId - ID da tarefa que foi alterada
 * @param {Object} settings - Configurações do projeto
 * @param {Set} visited - Set de IDs já processados (para evitar ciclos infinitos)
 * @returns {Array} Array de updates [{taskId, startDate, endDate}, ...]
 */
export const propagateDependencyChanges = (flatTasks: Task[], changedTaskId: string, settings: DateSettings, visited: Set<string> = new Set()): CascadeUpdate[] => {
  const updates: CascadeUpdate[] = [];

  // Proteção contra ciclos: se já processamos esta tarefa, parar
  if (visited.has(changedTaskId)) {
    return updates;
  }
  visited.add(changedTaskId);

  // Encontrar todas as tarefas que dependem da tarefa alterada
  const successors = flatTasks.filter(t =>
    t.dependencies?.includes(changedTaskId)
  );

  for (const successor of successors) {
    // Proteção extra: não processar sucessor se já foi visitado
    if (visited.has(successor.id)) {
      continue;
    }

    // Recalcular datas do sucessor baseado em TODAS suas predecessoras
    const predecessorTasks = successor.dependencies.map(depId =>
      flatTasks.find(t => t.id === depId)
    ).filter(Boolean) as Task[];

    // Usar os lags configurados no próprio sucessor
    const successorLags = successor.dependencyLags || {};

    const newDates = calculateDependentTaskDates(successor, predecessorTasks, settings, successorLags);
    if (newDates) {
      updates.push({ taskId: successor.id, ...newDates });

      // Continuar cascata recursivamente
      // Atualizar flatTasks temporariamente com as novas datas
      const updatedFlatTasks = flatTasks.map(t =>
        t.id === successor.id ? { ...t, ...newDates } : t
      );
      const nestedUpdates = propagateDependencyChanges(updatedFlatTasks, successor.id, settings, visited);
      updates.push(...nestedUpdates);
    }
  }

  return updates;
};

/**
 * Detecta ciclos de dependência
 * Verifica se adicionar novas dependências criaria um ciclo
 *
 * @param {Array} flatTasks - Lista flat de todas as tarefas
 * @param {string} taskId - ID da tarefa que está recebendo dependências
 * @param {Array} newDependencies - Array de IDs das novas dependências
 * @returns {boolean} true se detectou ciclo, false caso contrário
 */
export const detectDependencyCycle = (flatTasks: Task[], taskId: string, newDependencies: string[]): boolean => {
  const visited = new Set<string>();

  const hasCycle = (currentId: string, path: string[] = []): boolean => {
    if (path.includes(currentId)) return true;
    if (visited.has(currentId)) return false;

    visited.add(currentId);
    const task = flatTasks.find(t => t.id === currentId);
    if (!task?.dependencies) return false;

    for (const depId of task.dependencies) {
      if (hasCycle(depId, [...path, currentId])) return true;
    }
    return false;
  };

  // Verificar se qualquer nova dependência criaria um ciclo
  for (const depId of newDependencies) {
    if (hasCycle(depId, [taskId])) return true;
  }

  return false;
};

/**
 * Valida se a primeira tarefa pode ter predecessoras
 * Primeira linha não pode ter predecessora (regra de negócio)
 *
 * @param {number} rowIndex - Índice da linha (0-based)
 * @returns {boolean} true se pode ter predecessoras
 */
export const canHavePredecessors = (rowIndex: number): boolean => {
  return rowIndex > 0;
};

/**
 * Verifica se uma tarefa é ancestral de outra (pai, avô, bisavô, etc.)
 *
 * @param {Array} flatTasks - Lista flat de todas as tarefas com parentId
 * @param {string} potentialAncestorId - ID do potencial ancestral
 * @param {string} taskId - ID da tarefa a verificar
 * @returns {boolean} true se potentialAncestorId é ancestral de taskId
 */
export const isAncestorOf = (flatTasks: Task[], potentialAncestorId: string, taskId: string): boolean => {
  const task = flatTasks.find(t => t.id === taskId);
  if (!task || !task.parentId) return false;

  // Verificar se o pai direto é o potencial ancestral
  if (task.parentId === potentialAncestorId) return true;

  // Verificar recursivamente os ancestrais
  return isAncestorOf(flatTasks, potentialAncestorId, task.parentId);
};

/**
 * Verifica se uma tarefa é descendente de outra (filha, neta, bisneta, etc.)
 *
 * @param {Array} flatTasks - Lista flat de todas as tarefas com parentId
 * @param {string} potentialDescendantId - ID do potencial descendente
 * @param {string} taskId - ID da tarefa a verificar
 * @returns {boolean} true se potentialDescendantId é descendente de taskId
 */
export const isDescendantOf = (flatTasks: Task[], potentialDescendantId: string, taskId: string): boolean => {
  // Se potentialDescendant tem taskId como ancestral, então é descendente
  return isAncestorOf(flatTasks, taskId, potentialDescendantId);
};

/**
 * Converte string de predecessoras (números de linha com lag opcional) para IDs de tarefas
 * Filtra referências inválidas automaticamente, incluindo:
 * - Linhas fora do range
 * - A própria tarefa
 * - Linhas posteriores à tarefa atual
 * - Ancestrais da tarefa (pai, avô, etc.) - evita dependência circular em épicos
 * - Descendentes da tarefa (filhos, netos, etc.) - evita dependência circular
 *
 * Sintaxe suportada: "1 +2d, 2, 3 -1d" onde:
 * - Número = linha da predecessora
 * - +Nd = lag de N dias úteis após término
 * - -Nd = lead de N dias (sobreposição)
 * - Omitir = usa lag padrão (+1d)
 *
 * @param {string} value - String com números de linha e lag (ex: "1 +2d, 2, 3 -1d")
 * @param {number} rowIndex - Índice da tarefa atual
 * @param {Array} flatTasks - Lista flat de todas as tarefas
 * @returns {Object} { dependencies: Array<string>, dependencyLags: Object }
 */
export const parsePredecessorString = (value: string | null | undefined, rowIndex: number, flatTasks: Task[]): ParsedPredecessorResult => {
  const emptyResult: ParsedPredecessorResult = { dependencies: [], dependencyLags: {} };

  if (!value || value.trim() === '') return emptyResult;

  const currentTask = flatTasks[rowIndex];
  if (!currentTask) return emptyResult;

  // Épicos (tarefas com filhas) não podem ter predecessoras
  if (isEpicTask(currentTask)) {
    return emptyResult;
  }

  const items = value.split(/[,;]/).map(s => s.trim()).filter(Boolean);
  const dependencies: string[] = [];
  const dependencyLags: Record<string, string> = {};

  for (const item of items) {
    // Usar parsePredecessorWithLag do lagUtils para parsear "1 +2d"
    const parsed = parsePredecessorWithLag(item);
    if (!parsed) continue;

    const { rowNum, lag } = parsed;

    // Validações de referência
    // Deve ser um número válido (dentro do range de tarefas)
    if (rowNum < 1 || rowNum > flatTasks.length) continue;
    // Não pode ser a própria tarefa
    if (rowNum === rowIndex + 1) continue;
    // Não pode ser maior ou igual à linha atual (evita ciclos simples - FS apenas para linhas anteriores)
    if (rowNum >= rowIndex + 1) continue;

    // Verificar se a predecessora é um ancestral da tarefa atual
    const predecessorTask = flatTasks[rowNum - 1];
    if (predecessorTask && isAncestorOf(flatTasks, predecessorTask.id, currentTask.id)) {
      continue;
    }

    // Verificar se a predecessora é um descendente da tarefa atual
    if (predecessorTask && isDescendantOf(flatTasks, predecessorTask.id, currentTask.id)) {
      continue;
    }

    // Adicionar dependência válida
    const taskId = predecessorTask?.id;
    if (taskId) {
      dependencies.push(taskId);
      // Só armazenar lag se for diferente do padrão (otimização)
      if (lag && isValidLag(lag)) {
        dependencyLags[taskId] = lag;
      }
    }
  }

  return { dependencies, dependencyLags };
};

/**
 * Processa atualização completa de uma tarefa
 * Aplica todas as regras de negócio e retorna as atualizações necessárias
 *
 * @param {Object} task - Tarefa sendo atualizada
 * @param {Object} updates - Campos sendo atualizados (pode incluir dependencyLags)
 * @param {Array} flatTasks - Lista flat de todas as tarefas
 * @param {Object} settings - Configurações do projeto
 * @returns {Object} { taskUpdates, cascadeUpdates }
 */
export const processTaskUpdate = (task: Task, updates: TaskDateUpdates, flatTasks: Task[], settings: DateSettings): ProcessTaskUpdateResult => {
  // Normalizar datas
  const normalizedUpdates = validateAndNormalizeTaskDates(task, updates, settings);

  // Se dependencyLags foi passado no update, incluir
  if (updates.dependencyLags !== undefined) {
    normalizedUpdates.dependencyLags = updates.dependencyLags;
  }

  // Se está adicionando/modificando dependências, validar e calcular datas
  if (updates.dependencies !== undefined) {
    const newDependencies = updates.dependencies || [];

    // Épicos (tarefas com filhas) não podem ter predecessoras
    if (newDependencies.length > 0 && isEpicTask(task)) {
      console.warn('Épicos não podem ter predecessoras! Dependências não serão salvas.');
      delete normalizedUpdates.dependencies;
      delete normalizedUpdates.dependencyLags;
      return { taskUpdates: normalizedUpdates, cascadeUpdates: [] };
    }

    // Validar ciclos de dependência antes de processar
    if (newDependencies.length > 0 && detectDependencyCycle(flatTasks, task.id, newDependencies)) {
      console.warn('Ciclo de dependência detectado! Dependências não serão salvas.');
      // Remover dependências do update para evitar ciclo
      delete normalizedUpdates.dependencies;
      delete normalizedUpdates.dependencyLags;
      return { taskUpdates: normalizedUpdates, cascadeUpdates: [] };
    }

    if (newDependencies.length > 0) {
      // Buscar tarefas predecessoras
      const predecessors = newDependencies.map(depId =>
        flatTasks.find(t => t.id === depId)
      ).filter(Boolean) as Task[];

      // Usar dependencyLags do update ou existente da tarefa
      const effectiveLags = (normalizedUpdates.dependencyLags || task.dependencyLags || {}) as Record<string, DependencyLag | string>;

      // Calcular novas datas baseadas nas predecessoras COM lag
      const dependentDates = calculateDependentTaskDates(
        { ...task, duration: (normalizedUpdates.duration || task.duration) as string | null },
        predecessors,
        settings,
        effectiveLags
      );

      if (dependentDates) {
        normalizedUpdates.startDate = dependentDates.startDate;
        normalizedUpdates.endDate = dependentDates.endDate;
      }
    }
    // Se removeu todas as dependências, manter datas atuais e limpar lags
    if (newDependencies.length === 0) {
      normalizedUpdates.dependencyLags = {};
    }
  }

  // Se tem dependências (existentes ou recém-adicionadas) e está tentando mudar startDate manualmente,
  // recalcular baseado nas dependências para garantir consistência
  const effectiveDependencies = updates.dependencies !== undefined ? updates.dependencies : task.dependencies;
  const effectiveLags = (normalizedUpdates.dependencyLags || task.dependencyLags || {}) as Record<string, DependencyLag | string>;

  if (effectiveDependencies?.length > 0 && updates.startDate) {
    const predecessors = effectiveDependencies.map(depId =>
      flatTasks.find(t => t.id === depId)
    ).filter(Boolean) as Task[];

    const dependentDates = calculateDependentTaskDates(
      { ...task, duration: (normalizedUpdates.duration || task.duration) as string | null },
      predecessors,
      settings,
      effectiveLags
    );

    if (dependentDates) {
      // Se a data calculada por dependência é diferente da manual, usar a de dependência
      // (tarefa com dependência não pode ter startDate antes do término da predecessora)
      const calculatedStart = new Date(dependentDates.startDate).getTime();
      const manualStart = new Date(normalizedUpdates.startDate!).getTime();

      if (calculatedStart > manualStart) {
        normalizedUpdates.startDate = dependentDates.startDate;
        normalizedUpdates.endDate = dependentDates.endDate;
      }
    }
  }

  // Calcular cascata se a tarefa foi modificada de forma que afeta sucessoras
  const taskUpdates = normalizedUpdates;
  let cascadeUpdates: CascadeUpdate[] = [];

  // Se endDate mudou, propagar para sucessoras
  if (normalizedUpdates.endDate || normalizedUpdates.startDate || normalizedUpdates.duration) {
    const updatedTask = { ...task, ...normalizedUpdates } as Task;
    const updatedFlatTasks = flatTasks.map(t =>
      t.id === task.id ? updatedTask : t
    );
    cascadeUpdates = propagateDependencyChanges(updatedFlatTasks, task.id, settings);
  }

  // customFields nunca é responsabilidade de regras de data — propagar explicitamente
  // para evitar regressão silenciosa caso alguma normalização futura apague chaves.
  if ((updates as Record<string, unknown>).customFields !== undefined) {
    (taskUpdates as Record<string, unknown>).customFields = (updates as Record<string, unknown>).customFields;
  }

  return { taskUpdates, cascadeUpdates };
};

/**
 * Recalcula todas as tarefas quando settings mudam
 *
 * @param {Array} flatTasks - Lista flat de todas as tarefas
 * @param {Object} settings - Novas configurações do projeto
 * @returns {Array} Array de updates [{taskId, startDate, endDate}, ...]
 */
export const recalculateAllTasks = (flatTasks: Task[], settings: DateSettings): CascadeUpdate[] => {
  const { workDays, holidays, hoursPerDay } = settings;
  const updates: CascadeUpdate[] = [];

  // Primeiro: recalcular tarefas SEM dependências (ajustar startDate e endDate)
  flatTasks.forEach(task => {
    if (task.startDate && task.duration && (!task.dependencies || task.dependencies.length === 0)) {
      // Ajustar startDate para dia útil se necessário
      const adjustedStartDate = ensureWorkDay(task.startDate, workDays, holidays);
      const newEndDate = calculateEndDate(adjustedStartDate, task.duration, workDays, holidays, hoursPerDay);

      updates.push({
        taskId: task.id,
        startDate: adjustedStartDate,
        endDate: newEndDate
      });
    }
  });

  // Atualizar flatTasks temporariamente com as novas datas
  let updatedFlatTasks = flatTasks.map(task => {
    const update = updates.find(u => u.taskId === task.id);
    return update ? { ...task, startDate: update.startDate, endDate: update.endDate } as Task : task;
  });

  // Segundo: recalcular tarefas COM dependências (cascata)
  updatedFlatTasks.forEach(task => {
    if (task.dependencies && task.dependencies.length > 0 && task.duration) {
      const predecessors = task.dependencies.map(depId =>
        updatedFlatTasks.find(t => t.id === depId)
      ).filter(Boolean) as Task[];

      // Usar os lags configurados na própria tarefa
      const taskLags = task.dependencyLags || {};

      const newDates = calculateDependentTaskDates(task, predecessors, settings, taskLags);
      if (newDates) {
        updates.push({
          taskId: task.id,
          ...newDates
        });

        // Atualizar para próximas iterações
        updatedFlatTasks = updatedFlatTasks.map(t =>
          t.id === task.id ? { ...t, ...newDates } as Task : t
        );
      }
    }
  });

  return updates;
};

/**
 * Calcula datas do pai baseado nas datas das filhas (lógica de Épico)
 * startDate = menor startDate das filhas
 * endDate = maior endDate das filhas
 * duration = calculada entre startDate e endDate
 *
 * @param {Object} parentTask - Tarefa pai
 * @param {Array} children - Array de tarefas filhas
 * @param {Object} settings - Configurações do projeto
 * @returns {Object|null} { startDate, endDate, duration } ou null se não tiver filhas válidas
 */
export const calculateParentDatesFromChildren = (parentTask: Task, children: Task[], settings: DateSettings): { startDate: Date; endDate: Date; duration: string } | null => {
  if (!children || children.length === 0) return null;

  const { workDays, holidays } = settings;

  let minStart: Date | null = null;
  let maxEnd: Date | null = null;

  children.forEach(child => {
    if (child.startDate) {
      const childStart = new Date(child.startDate);
      if (!minStart || childStart < minStart) {
        minStart = childStart;
      }
    }
    if (child.endDate) {
      const childEnd = new Date(child.endDate);
      if (!maxEnd || childEnd > maxEnd) {
        maxEnd = childEnd;
      }
    }
  });

  if (!minStart || !maxEnd) return null;

  const duration = calculateDuration(minStart, maxEnd, workDays, holidays);

  return {
    startDate: minStart,
    endDate: maxEnd,
    duration
  };
};

/**
 * Calcula progresso do pai como média dos progressos das filhas
 * Se as filhas tiverem durações diferentes, faz média ponderada
 *
 * @param {Array} children - Array de tarefas filhas
 * @returns {number} Progresso médio (0-100)
 */
export const calculateParentProgress = (children: Task[]): number => {
  if (!children || children.length === 0) return 0;

  // Calcular média simples dos progressos
  const totalProgress = children.reduce((sum: number, child: Task) => {
    return sum + (child.progress || 0);
  }, 0);

  return Math.round(totalProgress / children.length);
};

/**
 * Propaga alterações de uma tarefa filha para seus ancestrais
 * Recalcula datas e progresso de todos os pais na hierarquia
 *
 * @param {Array} flatTasks - Lista flat de todas as tarefas com parentId
 * @param {string} childTaskId - ID da tarefa filha que foi alterada
 * @param {Object} settings - Configurações do projeto
 * @returns {Array} Array de updates para os pais [{taskId, startDate, endDate, duration, progress}, ...]
 */
export const propagateToParents = (flatTasks: Task[], childTaskId: string, settings: DateSettings): CascadeUpdate[] => {
  const updates: CascadeUpdate[] = [];
  const task = flatTasks.find(t => t.id === childTaskId);

  if (!task || !task.parentId) return updates;

  // Encontrar o pai
  const parent = flatTasks.find(t => t.id === task.parentId);
  if (!parent) return updates;

  // Encontrar todas as filhas do pai (irmãs da tarefa alterada)
  const siblings = flatTasks.filter(t => t.parentId === parent.id);

  // Calcular novas datas do pai
  const newDates = calculateParentDatesFromChildren(parent, siblings, settings);

  // Calcular novo progresso do pai (sempre, mesmo se não houver datas)
  const newProgress = calculateParentProgress(siblings);

  // Sempre atualizar o pai se houver datas OU se o progresso mudou
  const parentUpdate: CascadeUpdate = {
    taskId: parent.id,
    progress: newProgress
  };

  // Adicionar datas se disponíveis
  if (newDates) {
    parentUpdate.startDate = newDates.startDate;
    parentUpdate.endDate = newDates.endDate;
    parentUpdate.duration = newDates.duration;
  }

  updates.push(parentUpdate);

  // Continuar propagando para cima (avô, bisavô, etc.)
  if (parent.parentId) {
    // Atualizar flatTasks temporariamente com os novos dados do pai
    const updatedFlatTasks = flatTasks.map(t =>
      t.id === parent.id ? { ...t, ...parentUpdate } as Task : t
    );
    const ancestorUpdates = propagateToParents(updatedFlatTasks, parent.id, settings);
    updates.push(...ancestorUpdates);
  }

  return updates;
};

/**
 * Verifica se uma tarefa é um épico (tem filhas)
 * Épicos não podem ter suas datas editadas diretamente
 *
 * @param {Object} task - Tarefa a verificar
 * @returns {boolean} true se a tarefa tem filhas
 */
export const isEpicTask = (task: Task): boolean => {
  return task.hasChildren || (task.children && task.children.length > 0) || false;
};

/**
 * Limpa predecessoras inválidas de uma tarefa após mudança de hierarquia
 * Remove predecessoras que são agora ancestrais ou descendentes da tarefa
 *
 * @param {string} taskId - ID da tarefa
 * @param {Array} flatTasks - Lista flat de todas as tarefas após a mudança de hierarquia
 * @returns {Object|null} { cleanedDependencies, removedCount } ou null se não precisou limpar
 */
export const cleanInvalidPredecessors = (taskId: string, flatTasks: Task[]): CleanedPredecessorsResult | null => {
  const task = flatTasks.find(t => t.id === taskId);
  if (!task || !task.dependencies || task.dependencies.length === 0) {
    return null;
  }

  const originalCount = task.dependencies.length;

  // Filtrar predecessoras válidas (não são ancestrais nem descendentes)
  const cleanedDependencies = task.dependencies.filter(depId => {
    // Verificar se a predecessora é um ancestral da tarefa atual
    if (isAncestorOf(flatTasks, depId, taskId)) {
      return false;
    }

    // Verificar se a predecessora é um descendente da tarefa atual
    if (isDescendantOf(flatTasks, depId, taskId)) {
      return false;
    }

    return true;
  });

  const removedCount = originalCount - cleanedDependencies.length;

  if (removedCount > 0) {
    return { cleanedDependencies, removedCount };
  }

  return null;
};

/**
 * Valida se uma tarefa pode ter predecessoras
 * Épicos (tarefas com filhas) não devem ter predecessoras próprias
 *
 * @param {Object} task - Tarefa a verificar
 * @returns {boolean} true se a tarefa pode ter predecessoras
 */
export const canTaskHavePredecessors = (task: Task): boolean => {
  // Épicos não podem ter predecessoras
  if (isEpicTask(task)) {
    return false;
  }
  return true;
};

// ============================================================
// Critical Path Method (CPM)
// ============================================================

export interface CriticalPathResult {
  criticalTaskIds: Set<string>;
  nearCriticalTaskIds: Set<string>;
  projectDuration: number;
  taskFloats: Map<string, number>;
}

/**
 * Threshold (em dias úteis) abaixo do qual uma tarefa não-crítica é considerada
 * "near-critical" (sub-crítica). Convenção Primavera-style.
 */
export const NEAR_CRITICAL_FLOAT_THRESHOLD = 3;

/**
 * Converte string de duração para dias úteis.
 * Versão local para evitar import circular com portfolioService.
 */
const parseDurationToDaysLocal = (duration: string | null): number => {
  if (!duration) return 0;
  const match = duration.match(/^([\d.]+)\s*([dwhm]?)$/i);
  if (!match) return 0;
  const value = parseFloat(match[1]);
  const unit = (match[2] || 'd').toLowerCase();
  if (unit === 'h') return value / 8;
  if (unit === 'd') return value;
  if (unit === 'w') return value * 5;
  if (unit === 'm') return value * 20;
  return value;
};

/**
 * Calcula o caminho crítico do projeto usando o Critical Path Method.
 *
 * 1. Forward pass: ES/EF para cada tarefa
 * 2. Backward pass: LS/LF a partir do fim do projeto
 * 3. Float = LS - ES; tarefas com float 0 são críticas
 */
export const calculateCriticalPath = (flatTasks: Task[], settings: DateSettings): CriticalPathResult => {
  // Filtrar: só tarefas-folha (sem children), excluir groups/epics
  const leafTasks = flatTasks.filter(t =>
    !isEpicTask(t) && t.type !== 'group'
  );

  if (leafTasks.length === 0) {
    return {
      criticalTaskIds: new Set(),
      nearCriticalTaskIds: new Set(),
      projectDuration: 0,
      taskFloats: new Map(),
    };
  }

  const taskMap = new Map(leafTasks.map(t => [t.id, t]));

  // Build successors map
  const successors = new Map<string, string[]>();
  for (const task of leafTasks) {
    for (const depId of (task.dependencies || [])) {
      if (!taskMap.has(depId)) continue;
      if (!successors.has(depId)) successors.set(depId, []);
      successors.get(depId)!.push(task.id);
    }
  }

  // Duration in days per task
  const dur = new Map<string, number>();
  for (const task of leafTasks) {
    dur.set(task.id, parseDurationToDaysLocal(task.duration));
  }

  // Topological sort (Kahn's algorithm)
  const inDegree = new Map<string, number>();
  for (const task of leafTasks) {
    const validDeps = (task.dependencies || []).filter(d => taskMap.has(d));
    inDegree.set(task.id, validDeps.length);
  }

  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    sorted.push(id);
    for (const succId of (successors.get(id) || [])) {
      inDegree.set(succId, (inDegree.get(succId) || 0) - 1);
      if (inDegree.get(succId) === 0) queue.push(succId);
    }
  }

  // Forward pass: ES (Early Start), EF (Early Finish)
  const ES = new Map<string, number>();
  const EF = new Map<string, number>();

  for (const id of sorted) {
    const task = taskMap.get(id)!;
    const validDeps = (task.dependencies || []).filter(d => taskMap.has(d));
    const es = validDeps.length > 0
      ? Math.max(...validDeps.map(d => EF.get(d) || 0))
      : 0;
    ES.set(id, es);
    EF.set(id, es + (dur.get(id) || 0));
  }

  // Project duration
  const projectDuration = Math.max(...Array.from(EF.values()), 0);

  // Backward pass: LS (Late Start), LF (Late Finish)
  const LS = new Map<string, number>();
  const LF = new Map<string, number>();

  // Reverse order
  for (let i = sorted.length - 1; i >= 0; i--) {
    const id = sorted[i];
    const succs = (successors.get(id) || []).filter(s => taskMap.has(s));
    const lf = succs.length > 0
      ? Math.min(...succs.map(s => LS.get(s) ?? projectDuration))
      : projectDuration;
    LF.set(id, lf);
    LS.set(id, lf - (dur.get(id) || 0));
  }

  // Calculate float and identify critical / near-critical tasks
  const taskFloats = new Map<string, number>();
  const criticalTaskIds = new Set<string>();
  const nearCriticalTaskIds = new Set<string>();

  for (const task of leafTasks) {
    const totalFloat = (LS.get(task.id) || 0) - (ES.get(task.id) || 0);
    taskFloats.set(task.id, Math.round(totalFloat * 10) / 10);

    const hasDurationOrIsMilestone = (dur.get(task.id) || 0) > 0 || task.type === 'milestone';
    if (!hasDurationOrIsMilestone) continue;

    if (Math.abs(totalFloat) < 0.01) {
      // Critical: float 0 (ou muito próximo)
      criticalTaskIds.add(task.id);
    } else if (totalFloat > 0 && totalFloat <= NEAR_CRITICAL_FLOAT_THRESHOLD) {
      // Near-critical (sub-crítica): 0 < float ≤ threshold
      nearCriticalTaskIds.add(task.id);
    }
  }

  return { criticalTaskIds, nearCriticalTaskIds, projectDuration, taskFloats };
};
